/**
 * SINGLE SOURCE OF TRUTH for all FRONTEND country data.
 *
 * To add/edit a TRACKABLE (appointment) country, this array is NOT the only place —
 * mirror the change in the two backend catalogs:
 *   - services/backend/src/GetViza.Shared/Appointments/VisardCatalog.cs (status + message parsing keywords)
 *   - services/backend/src/GetViza.Notifications.Api/Configuration/ManualNotificationCatalog.cs (admin notify + email booking links)
 *
 * INVARIANT: `name` MUST equal the visa_centers_config key (e.g. "Çekya", "Yunanistan").
 * The appointment center lookups (AppointmentsTrackPage getJurisdictionInfo and the
 * AdminPage selection-edit dropdown) key visa_centers_config by this exact `name`.
 * If a config key ever diverges from the display name, add an optional `configKey`
 * field here — NEVER reintroduce a separate name→key map (that drift caused the
 * "Çekya" vs "Çek Cumhuriyeti" bug where centers stopped showing).
 *
 * Field reference:
 *   code         2-letter code; used for flagcdn URLs and booking lookup
 *   name         Turkish display name; equals the visa_centers_config key
 *   enName       English name (cover letter)
 *   flag         emoji flag
 *   aliases      EXTRA normalized match tokens beyond what name/enName already yield.
 *                Matching uses normalizeCountryName() which takes the FIRST WORD, ascii,
 *                lowercased — so "Çek Cumhuriyeti" -> "cek". Index builders auto-add the
 *                first-word + full ascii of name & enName; only list the historical
 *                extras here (e.g. usa/abd, birlesik, isvic, czechia, hrvatistan).
 *   appointment  trackable via VISARD -> feeds VISARDBOT_COUNTRIES
 *   reservation  'available' | 'info' | 'hidden' | null  (PaymentPage add-on logic)
 *   idata        true for İtalya/Almanya (+ idataUrl); reservation stays null
 *   rejectionOption  belongs to WelcomePage's rejection-country dropdown set
 *   booking      optional { links:[{label,url}], note? } for VisardCountryCard
 */

const TURKISH_TO_ASCII = {
  'İ': 'i', 'ı': 'i', 'ş': 's', 'Ş': 's',
  'ğ': 'g', 'Ğ': 'g', 'ü': 'u', 'Ü': 'u',
  'ö': 'o', 'Ö': 'o', 'ç': 'c', 'Ç': 'c',
};

export function foldTurkish(str) {
  let out = String(str);
  for (const [turkish, ascii] of Object.entries(TURKISH_TO_ASCII)) {
    out = out.replace(new RegExp(turkish, 'g'), ascii);
  }
  return out;
}

// Matches the historical util: Turkish->ascii, lowercase, trim, FIRST WORD only.
export function normalizeCountryName(name) {
  if (!name) return '';
  return foldTurkish(name).toLowerCase().trim().split(' ')[0];
}

// Full ascii-lowercase (whole string, not just first word) for substring/equality checks.
function asciiLower(str) {
  if (!str) return '';
  return foldTurkish(str).toLowerCase().trim();
}

const vfs = (cc) => ({ links: [{ label: 'VFS Global', url: `https://visa.vfsglobal.com/tur/tr/${cc}/login` }] });

export const COUNTRIES = [
  // ---- Appointment (VISARD-trackable) countries — reservation: 'available' ----
  { code: 'BE', name: 'Belçika', enName: 'Belgium', flag: '🇧🇪', aliases: [], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [
      { label: 'VOW Portal', url: 'https://visaonweb.diplomatie.be/' },
      { label: 'VFS Global', url: 'https://visa.vfsglobal.com/tur/tr/bel/login' },
    ], note: 'Önce VOW portalı üzerinden başvuru formunu doldurun, ardından VFS portalına yönlendirileceksiniz.' } },
  { code: 'CZ', name: 'Çekya', enName: 'Czech Republic', flag: '🇨🇿', aliases: ['czechia', 'cek'], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('cze') },
  { code: 'NL', name: 'Hollanda', enName: 'Netherlands', flag: '🇳🇱', aliases: ['holland'], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('nld') },
  { code: 'NO', name: 'Norveç', enName: 'Norway', flag: '🇳🇴', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('nor') },
  { code: 'DK', name: 'Danimarka', enName: 'Denmark', flag: '🇩🇰', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('dnk') },
  { code: 'FI', name: 'Finlandiya', enName: 'Finland', flag: '🇫🇮', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('fin') },
  { code: 'SE', name: 'İsveç', enName: 'Sweden', flag: '🇸🇪', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('swe') },
  { code: 'LU', name: 'Lüksemburg', enName: 'Luxembourg', flag: '🇱🇺', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('lux') },
  { code: 'EE', name: 'Estonya', enName: 'Estonia', flag: '🇪🇪', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('est') },
  { code: 'SI', name: 'Slovenya', enName: 'Slovenia', flag: '🇸🇮', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('svn') },
  { code: 'LV', name: 'Letonya', enName: 'Latvia', flag: '🇱🇻', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('lva') },
  { code: 'MT', name: 'Malta', enName: 'Malta', flag: '🇲🇹', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('mlt') },
  { code: 'BG', name: 'Bulgaristan', enName: 'Bulgaria', flag: '🇧🇬', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('bgr') },
  { code: 'FR', name: 'Fransa', enName: 'France', flag: '🇫🇷', aliases: [], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [{ label: 'VFS Global', url: 'https://visa.vfsglobal.com/tur/en/fra/login' }] } },
  { code: 'CH', name: 'İsviçre', enName: 'Switzerland', flag: '🇨🇭', aliases: ['isvic', 'swiss'], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('che') },
  { code: 'ES', name: 'İspanya', enName: 'Spain', flag: '🇪🇸', aliases: ['espana'], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [{ label: 'BLS Spain', url: 'https://turkey.blsspainglobal.com/Global/account/login' }], note: 'BLS International üzerinden randevu alınabilir.' } },
  { code: 'LT', name: 'Litvanya', enName: 'Lithuania', flag: '🇱🇹', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('ltu') },
  { code: 'SK', name: 'Slovakya', enName: 'Slovakia', flag: '🇸🇰', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('svk') },
  { code: 'HR', name: 'Hırvatistan', enName: 'Croatia', flag: '🇭🇷', aliases: ['hrvatistan'], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [{ label: 'VFS Global', url: 'https://visa.vfsglobal.com/tur/en/hrv/login' }] } },
  { code: 'GR', name: 'Yunanistan', enName: 'Greece', flag: '🇬🇷', aliases: [], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [{ label: 'Kosmos Vize', url: 'https://basvuru.kosmosvize.com.tr/appointmentForm' }] } },
  { code: 'PL', name: 'Polonya', enName: 'Poland', flag: '🇵🇱', aliases: [], appointment: true, reservation: 'available', rejectionOption: true, booking: vfs('pol') },
  { code: 'IT', name: 'İtalya', enName: 'Italy', flag: '🇮🇹', aliases: [], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [{ label: 'iData', url: 'https://it-tr-appointment.idata.com.tr/tr/membership/login' }] } },
  // Macaristan Türkiye'de VFS değil AS Visa üzerinden randevu veriyor (Portekiz gibi).
  { code: 'HU', name: 'Macaristan', enName: 'Hungary', flag: '🇭🇺', aliases: [], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [{ label: 'AS Visa', url: 'https://www.as-visa.com/login' }] } },
  // Portekiz: randevu AS Visa üzerinden (Macaristan gibi).
  { code: 'PT', name: 'Portekiz', enName: 'Portugal', flag: '🇵🇹', aliases: [], appointment: true, reservation: 'available', rejectionOption: true,
    booking: { links: [{ label: 'AS Visa', url: 'https://www.as-visa.com/login' }] } },
  // ABD: Schengen dışı. Rota tablosunda tek kategori var (B1/B2) ve merkezler İstanbul + Ankara;
  // katalog anahtarı bu `name` ile eşleşmek zorunda.
  { code: 'US', name: 'Amerika', enName: 'United States', flag: '🇺🇸', aliases: ['usa', 'abd'], appointment: true, reservation: 'available', rejectionOption: false,
    booking: { links: [{ label: 'US Visa Scheduling', url: 'https://atlasauth.b2clogin.com/atlasauth.onmicrosoft.com/oauth2/v2.0/authorize?p=B2C_1A_ATOPRODUCTION_ATLAS_CLAIM_PROFILE&client_id=607d08d6-b63b-4735-ad82-05dfcff7efa4&nonce=defaultNonce&redirect_uri=https%3A%2F%2Fwww.usvisascheduling.com%2Ftr-TR%2FSignIn&scope=openid&response_type=id_token&prompt=login&ui_locales=tr-TR' }] } },

  // ---- iData countries (appointments handled on iData, not reservation) ----
  { code: 'DE', name: 'Almanya', enName: 'Germany', flag: '🇩🇪', aliases: [], appointment: false, reservation: null, idata: true, idataUrl: 'https://de-tr-appointment.idata.com.tr/tr', rejectionOption: true },

  // ---- Reservation hidden (add-on not offered) ----
  { code: 'GB', name: 'İngiltere', enName: 'England', flag: '🇬🇧', aliases: ['birlesik'], appointment: false, reservation: 'hidden', rejectionOption: false },

  // ---- Reservation info (not available yet) ----
  { code: 'RO', name: 'Romanya', enName: 'Romania', flag: '🇷🇴', aliases: [], appointment: false, reservation: 'info', rejectionOption: true },

  // ---- Other referenced countries (flags / English name / display map) ----
  { code: 'AT', name: 'Avusturya', enName: 'Austria', flag: '🇦🇹', aliases: [], appointment: false, reservation: null, rejectionOption: true },
  { code: 'IS', name: 'İzlanda', enName: 'Iceland', flag: '🇮🇸', aliases: [], appointment: false, reservation: null, rejectionOption: true },
  { code: 'LI', name: 'Lihtenştayn', enName: 'Liechtenstein', flag: '🇱🇮', aliases: [], appointment: false, reservation: null, rejectionOption: false },
  { code: 'IE', name: 'İrlanda', enName: 'Ireland', flag: '🇮🇪', aliases: [], appointment: false, reservation: null, rejectionOption: false },
  { code: 'CY', name: 'Kıbrıs', enName: 'Cyprus', flag: '🇨🇾', aliases: [], appointment: false, reservation: null, rejectionOption: false },
];

// ---- Indexes (built once) ----
const byAlias = {};   // normalized first-word token -> entry
const byName = {};    // exact Turkish name -> entry
const byNameLower = {}; // diacritic lowercase name/enName (incl. multi-word) -> entry
const byCode = {};    // upper code -> entry

for (const c of COUNTRIES) {
  byName[c.name] = c;
  byCode[c.code] = c;
  byNameLower[c.name.toLowerCase()] = c;
  if (c.enName) byNameLower[c.enName.toLowerCase()] = c;
  const tokens = new Set([
    normalizeCountryName(c.name),
    ...(c.enName ? [normalizeCountryName(c.enName)] : []),
    ...(c.aliases || []),
  ]);
  for (const t of tokens) {
    if (t && byAlias[t] === undefined) byAlias[t] = c;
  }
}

// ---- Derived lists ----
export const VISARDBOT_COUNTRIES = COUNTRIES
  .filter((c) => c.appointment)
  .map((c) => ({ code: c.code, name: c.name }));

export const RESERVATION_AVAILABLE_COUNTRIES = COUNTRIES
  .filter((c) => c.reservation === 'available')
  .map((c) => c.name);

export const COUNTRY_FLAGS_BY_NAME = COUNTRIES
  .filter((c) => c.rejectionOption)
  .reduce((acc, c) => { acc[c.name] = c.flag; return acc; }, {});

export const REJECTION_COUNTRY_OPTIONS = Object.keys(COUNTRY_FLAGS_BY_NAME)
  .sort((a, b) => a.localeCompare(b, 'tr'));

// ---- Lookups (token args are already normalizeCountryName()'d, for byte-compat) ----
export function findCountryByAlias(normToken) { return byAlias[normToken]; }
export function getReservationStatus(normToken) { return byAlias[normToken]?.reservation ?? null; }
export function isIdata(normToken) { return Boolean(byAlias[normToken]?.idata); }
export function getIdataUrl(normToken) { return byAlias[normToken]?.idataUrl; }
export function isHidden(normToken) { return byAlias[normToken]?.reservation === 'hidden'; }

export function getFlagByName(name) { return byName[name]?.flag; }

// Substring scan over ascii-folded country names (Telegram message feed).
export function getFlagByText(text) {
  const hay = asciiLower(text);
  for (const c of COUNTRIES) {
    if (c.flag && hay.includes(asciiLower(c.name))) return c.flag;
  }
  return '';
}

// raw -> English name. Handles exact name/enName (incl. "Çek Cumhuriyeti") and first-word alias.
export function getEnName(raw) {
  if (!raw) return raw;
  const lower = String(raw).toLowerCase().trim();
  if (byNameLower[lower]) return byNameLower[lower].enName || raw;
  const entry = byAlias[normalizeCountryName(raw)];
  return entry?.enName || raw;
}

export function getBooking(code) { return (code && byCode[code.toUpperCase()]?.booking) || null; }
